import { motion } from "framer-motion";
import { PARTICIPANTS, IMPACTS, IMPACT_SUMMARY, RECEIPTS } from "./data";
import { StatusChip, Kicker, DownloadReceiptBtn, LocalBoundary } from "./ui";

export const ActImpact = ({ view, scenario }) => {
    const focusId =
        scenario === "legacy" ? "settle" : scenario === "interpretation" ? "recv" : null;
    const viewFocus = view === "orig" ? "orig" : view === "recv" ? "recv" : null;

    return (
        <section data-testid="act-impact">
            <Kicker>Act 2 — Local impact assessment</Kicker>
            <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                The same change, four independent local assessments
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                Each participant runs its own locally hosted role algorithm against its
                own systems and evidence. Only bounded assessment results leave the
                custody boundary — no raw customer, transaction or system data is
                exposed.
            </p>

            {scenario === "legacy" && (
                <ScenarioNote testId="scenario-note-legacy">
                    Scenario: Legacy system not ready — the Settlement / Reporting Role
                    returns a RED result. This single result changes the final package
                    and the activation plan (see Activation).
                </ScenarioNote>
            )}
            {scenario === "interpretation" && (
                <ScenarioNote testId="scenario-note-interpretation">
                    Scenario: Interpretation conflict — the Receiving Institution cannot
                    distinguish RULES_ENGINE from AUTOMATED_WITHIN_MANDATE. The steward
                    resolves this by clarification, not by inspecting the participant's
                    systems.
                </ScenarioNote>
            )}

            {/* Network summary */}
            <div
                className="mt-6 flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-white px-4 py-3"
                data-testid="impact-summary-strip"
            >
                <span className="mr-2 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                    Network-level summary
                </span>
                {IMPACT_SUMMARY.map((s) => (
                    <StatusChip key={s.label} tone={s.tone} testId={`summary-${s.label.toLowerCase()}`}>
                        {s.count} {s.label}
                    </StatusChip>
                ))}
                <span className="ml-auto text-xs text-slate-400">
                    Not a vote — the steward remains authoritative for the standard.
                </span>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
                {PARTICIPANTS.map((p, i) => {
                    const imp = IMPACTS[p.id];
                    const highlight = focusId === p.id || viewFocus === p.id;
                    const dim =
                        (focusId && focusId !== p.id) || (viewFocus && viewFocus !== p.id);
                    return (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.08 * i, duration: 0.3 }}
                        >
                            <LocalBoundary highlight={highlight} dim={dim} testId={`impact-node-${p.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                        <p className="text-sm font-medium text-slate-900">{p.name}</p>
                                        <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
                                            Local role algorithm · separately operated
                                        </p>
                                    </div>
                                    <StatusChip tone={imp.tone} testId={`impact-result-${p.id}`}>
                                        {imp.result}
                                    </StatusChip>
                                </div>

                                <ul className="mt-3 space-y-1 text-sm text-slate-600">
                                    {imp.findings.map((f) => <li key={f}>· {f}</li>)}
                                </ul>

                                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-100 pt-3 text-xs">
                                    <Meta k="Local policy version" v={imp.meta.policyVersion} mono />
                                    <Meta k="Impacted components" v={imp.meta.impactedComponents} />
                                    <Meta k="Data availability" v={imp.meta.dataAvailability} />
                                    <Meta k="Test status" v={imp.meta.testStatus} />
                                    <Meta k="Proposed readiness date" v={imp.meta.readinessDate} mono />
                                    <Meta k="Unresolved questions" v={imp.meta.unresolvedQuestions} />
                                </dl>

                                <div className="mt-4 flex items-center justify-between gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
                                        Signed impact receipt
                                    </span>
                                    <DownloadReceiptBtn
                                        json={{
                                            ...RECEIPTS[1].json,
                                            assessing_role: p.name,
                                            local_policy_version: imp.meta.policyVersion,
                                            assessment_result: imp.result,
                                            proposed_readiness_date: imp.meta.readinessDate,
                                        }}
                                        filename={`impact-receipt-${p.id}.json`}
                                        testId={`download-impact-${p.id}`}
                                        label="Impact receipt"
                                    />
                                </div>
                            </LocalBoundary>
                        </motion.div>
                    );
                })}
            </div>

            <p className="mt-4 text-xs leading-relaxed text-slate-400">
                Bounded impact receipts only: assessment result, local policy version,
                impacted component count, data-availability status, test status,
                proposed readiness date and unresolved questions. Sensitive systems and
                transaction data remain local.
            </p>
        </section>
    );
};

function Meta({ k, v, mono }) {
    return (
        <div>
            <dt className="font-medium uppercase tracking-wide text-slate-400">{k}</dt>
            <dd className={`mt-0.5 text-slate-700 ${mono ? "font-mono" : ""}`}>{v}</dd>
        </div>
    );
}

function ScenarioNote({ children, testId }) {
    return (
        <div
            data-testid={testId}
            className="mt-4 rounded-sm border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-relaxed text-blue-950"
        >
            {children}
        </div>
    );
}
